import type { FC } from "react";
import { useMemo } from "react";

import { isEditing } from "@builder.io/sdk-react";
import classNames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import type { ProductsMap } from "@/libs/commerce/products";
import { shouldShowQuickAddToCart } from "@/utils/quickAddToCart";

import { Placeholder } from "../Placeholder/Placeholder";

import { ProductCard } from "./ProductCard";
import type { productCardInputs } from "./ProductCard.builder";

import styles from "./ProductCard.module.scss";

export type ProductCardWrapperProps = BuilderComponentProps<typeof productCardInputs>;

export const ProductCardWrapper: FC<ProductCardWrapperProps & { products?: ProductsMap }> = ({
  products,
  sku,
  trialSku,
  badge,
  attributes = {},
  secondaryButton,
  primaryButton,
  isPrimaryButtonHidden,
  isDescriptionHidden,
  isCurrentPriceHidden,
  isOldPriceHidden,
  isProductColorVariantsHidden,
  isNewBadgeHidden,
  isDiscountBadgeHidden,
  isFreeTrialProduct,
  isOutOfStockLabelHidden,
}) => {
  const { __ } = useTranslations();
  const { isQuickAddToCartCtaForSimpleSticksEnabled } = useConfig();

  const productSku = isFreeTrialProduct ? trialSku?.options?.trialProduct : sku?.options?.product;
  const product = productSku ? products?.[productSku] : null;

  const shouldShowQuickAddToCartForProduct = useMemo(() => {
    return shouldShowQuickAddToCart(product, isQuickAddToCartCtaForSimpleSticksEnabled ?? false);
  }, [product, isQuickAddToCartCtaForSimpleSticksEnabled]);

  const href = isFreeTrialProduct ? product?.trialPageUrl : primaryButton?.href || product?.pageUrl;

  const primaryButtonText = shouldShowQuickAddToCartForProduct
    ? __("product.button.quickaddtocart")
    : isFreeTrialProduct
      ? __("product.button.trynow")
      : primaryButton?.text || __("product.button.buynow");

  const primaryButtonConfig = {
    href,
    text: primaryButtonText,
    enabled: isFreeTrialProduct || !isPrimaryButtonHidden,
  };

  const secondaryButtonConfig =
    secondaryButton?.href && secondaryButton?.text
      ? {
          href: secondaryButton.href,
          text: secondaryButton.text,
        }
      : undefined;

  const newProductBadgeLabel = product?.isNew && !isNewBadgeHidden ? __("product.badge.new") : "";

  const badgeLeft = badge?.left || newProductBadgeLabel;

  if (isEditing() && !productSku) {
    return <Placeholder>Please fill product SKU.</Placeholder>;
  }

  return (
    <div {...attributes} className={classNames(styles.productCardBuilderWrapper, attributes.className)}>
      <ProductCard
        sku={productSku || ""}
        title={product?.title}
        primaryButton={primaryButtonConfig}
        badge={{
          left: badgeLeft,
          right: badge?.right,
        }}
        description={product?.description}
        image={{
          src: product?.image || "",
          alt: product?.altText || "",
        }}
        categoryId={product?.categoryIds[0] || ""}
        categoryName={product?.categoryNames[0] || ""}
        colors={!isProductColorVariantsHidden ? product?.colors : undefined}
        secondaryButton={secondaryButtonConfig}
        isCurrentPriceHidden={isCurrentPriceHidden}
        isOldPriceHidden={isOldPriceHidden}
        isDescriptionHidden={isDescriptionHidden}
        isDiscountBadgeHidden={isDiscountBadgeHidden}
        isFreeTrialProduct={isFreeTrialProduct}
        isProductColorVariantsHidden={isProductColorVariantsHidden}
        isBundle={product?.type === "BundleProduct"}
        shouldShowQuickAddToCart={shouldShowQuickAddToCartForProduct}
        isOutOfStockLabelHidden={isOutOfStockLabelHidden}
      />
    </div>
  );
};
